import React, { useCallback } from "react";
import {
  Box,
  Typography,
  Grid,
  FormControl,
  FormControlLabel,
  FormLabel,
  FormGroup,
  Checkbox,
  useTheme,
} from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../store/Index";
import { tokens } from "../../../../../theme";
import { StepComponentProps } from "../../AddBookingV2";
import type { AdditionalServiceItem } from "../../../../../types/package";
import StepHeader from "../StepHeader";
import { usePackagesAndServices } from "../usePackagesAndServices";

const ServicesStep: React.FC<StepComponentProps> = ({
  bookingData,
  setBookingData,
  isEdit,
  onUpdate,
  submitting,
  bookingType,
}) => {
  const theme = useTheme();
  const themeState = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, themeState);
  const color400 = colors.primary[400];

  const { additionalServicesConfig, loading } = usePackagesAndServices(bookingType);

  const handleAdditionalServiceChange = useCallback(
    (category: string, value: string, checked: boolean) => {
      setBookingData((prev) => {
        const updated = { ...prev };

        // Update the category selection
        if (checked) {
          updated[category] = [...((updated[category] as string[]) || []), value];
        } else {
          updated[category] = ((updated[category] as string[]) || []).filter(
            (val: string) => val !== value
          );
        }

        // Recalculate additional services total
        let additionalTotal = 0;
        const allAdditionalServices: AdditionalServiceItem[] = [];

        Object.keys(additionalServicesConfig).forEach((cat) => {
          const selected = updated[cat];
          if (Array.isArray(selected)) {
            selected.forEach((val) => {
              const service = additionalServicesConfig[cat]?.find((s) => s.value === val);
              if (service) {
                additionalTotal += service.price;
                allAdditionalServices.push({ ...service, category: cat });
              }
            });
          }
        });

        const packageTotal = prev.package_total || 0;
        const finalTotal = parseFloat((packageTotal + additionalTotal).toFixed(2));

        return {
          ...updated,
          additional_services_total: additionalTotal,
          additional_services: allAdditionalServices,
          total: finalTotal,
        };
      });
    },
    [additionalServicesConfig, setBookingData]
  );

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography sx={{ color: "text.secondary" }}>Loading services...</Typography>
      </Box>
    );
  }

  if (Object.keys(additionalServicesConfig).length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography sx={{ color: "text.secondary" }}>No additional services available.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 4 }}>
      <StepHeader
        title="Additional Services"
        isEdit={isEdit}
        onUpdate={onUpdate}
        submitting={submitting}
      />

      <Grid container spacing={2} sx={{ alignItems: "stretch" }}>
        {Object.entries(additionalServicesConfig).map(([category, services]) => (
          <Grid size={{ xs: 12, md: 6 }} key={category} sx={{ display: "flex" }}>
            <Box
              sx={{
                p: 2,
                borderRadius: "16px",
                border: `1px solid`,
                borderColor: "divider",
                backgroundColor: "background.paper",
                width: "100%",
              }}
            >
              <FormControl fullWidth>
                <FormLabel sx={{ mb: 1, fontWeight: 600, color: "text.primary" }}>
                  {category}
                </FormLabel>
                <FormGroup>
                  {services.map(({ value, label, price }) => (
                    <Box
                      key={`${category}-${value}`}
                      sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={
                              (bookingData[category] as string[])?.includes(value) || false
                            }
                            onChange={(e) =>
                              handleAdditionalServiceChange(category, value, e.target.checked)
                            }
                          />
                        }
                        label={<Typography variant="body2">{label}</Typography>}
                        sx={{ flexGrow: 1 }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary" }}>
                        ${price.toFixed(2)}
                      </Typography>
                    </Box>
                  ))}
                </FormGroup>
              </FormControl>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Services subtotal */}
      <Box
        sx={{
          p: 3, borderRadius: "16px", border: `1px solid`, borderColor: "divider",
          backgroundColor: "background.paper",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, color: "text.primary", mb: 1 }}>
          Services Subtotal
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 800, color: color400 }}>
          ${(bookingData.additional_services_total || 0).toFixed(2)}
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
          Package: ${(bookingData.package_total || 0).toFixed(2)} · Total: ${(bookingData.total || 0).toFixed(2)}
        </Typography>
      </Box>
    </Box>
  );
};

export default ServicesStep;